import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, LogOut } from "lucide-react";
import { useState, useEffect } from "react";
import { api, fileUrl } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const links = [
  { to: "/services", label: "Services" },
  { to: "/quote", label: "Instant Quote" },
  { to: "/booking", label: "Book" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/warranty", label: "Warranty" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [brand, setBrand] = useState(null);

  useEffect(() => {
    api.get("/settings/brand").then((r) => setBrand(r.data)).catch(() => {});
  }, []);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <header className="glass sticky top-0 z-50" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
          {brand?.logo_path ? (
            <img src={fileUrl(brand.logo_path)} alt="Tech Detailers" className="h-9 w-auto object-contain" />
          ) : (
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-md bg-[#FFB800] flex items-center justify-center text-black font-bold">T</div>
              <div className="font-display text-lg tracking-tight leading-none">Tech <span className="text-[#FFB800]">Detailers</span></div>
            </div>
          )}
          <div className="hidden sm:block border-l border-zinc-800 pl-3">
            <div className="overline text-[0.55rem] text-zinc-500">Car Care</div>
            <div className="text-[0.65rem] text-zinc-400 leading-tight">Navi Mumbai</div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `transition-colors ${isActive ? "text-[#FFB800]" : "text-zinc-300 hover:text-white"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 border border-zinc-800 rounded-full px-3 py-1.5 hover:border-[#FFB800] transition-colors"
                >
                  <div className="h-6 w-6 rounded-full bg-zinc-800 flex items-center justify-center text-xs">
                    {user.name?.[0] || user.email?.[0]}
                  </div>
                  <span className="text-sm hidden sm:inline">{user.name?.split(" ")[0] || "You"}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#141417] border-zinc-800 text-zinc-100">
                <DropdownMenuLabel className="text-xs text-zinc-400">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  Dashboard
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    Admin CRM
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button onClick={login} className="btn-outline text-sm" data-testid="login-button">Sign in</button>
          )}
          <Link to="/booking" className="btn-primary text-sm hidden sm:inline-flex" data-testid="cta-book">Book Now</Link>
          <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-zinc-800 px-6 py-3 space-y-2 bg-[#0A0A0C]">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="block py-2 text-zinc-300" data-testid={`mnav-${l.label.toLowerCase()}`}>
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}
